// What the Theme Builder previews against: one shared demo event, for
// every client, always.
//
// WHY A FIXED EVENT RATHER THAN THE CLIENT'S OWN
// The preview used to resolve the editing client's most recently published
// event. That event is arbitrary. If it has no transcript, no documents and
// no agenda, the tab strip is empty -- so the controls that theme the tab
// paint onto nothing, and setting a colour appears to do nothing at all.
// Same for a client with no published events, who got a dead end instead of
// a preview. A curated demo event guarantees every themable surface exists,
// and as a side effect stops preview traffic from firing analytics against
// a client's real event.
//
// WHY BOTH IDS MOVE TOGETHER
// `eventID` is scoped to a client, not global. The same ID under two clients
// resolves to two different events -- verified against Event/getDetailed:
// `2024121005` is "Clip Studio Walkthough" for client 1000000000 and
// "Salmon Recovery Funding Board" for 9375922947. So pinning the event ID
// while leaving the client's own clientID in place would silently preview a
// DIFFERENT, real event. It fails plausibly, which is worse than failing
// loudly. The pair is therefore inseparable.
//
// WHY THERE IS NO prefID HERE
// Preferences are client-scoped too: getPlayerPreference answers a
// cross-client prefID with a null row, which the player turns into fresh
// defaults plus a Sentry flush. Pinning the demo account means the editing
// client's preference can never be named by ID. It is handed to the player
// as an object instead -- see `public/theme-preview.html` and the player's
// `playerPref` launch option. That is also what makes UNSAVED edits
// previewable, which no ID could ever reference.
//
// Setup requirements for the event itself (what it must contain, and why)
// live in `.claude/plans/player-preference-editor/demo-preview-event.md`.

// The demo event. Published, with the Player Theming video as its media, and
// carrying all three list surfaces so every themable control has something to
// paint on. The override params below stay available for reproducing a
// client's report against their own event.
export const DEMO_CLIENT_ID = '1000000001';
export const DEMO_EVENT_ID = '2032011000';

/** `?previewClientID=` / `?previewEventID=` — override for one page load. */
const CLIENT_PARAM = 'previewClientID';
const EVENT_PARAM = 'previewEventID';

/**
 * The client/event pair the preview mounts.
 *
 * Overridable per page load so the demo event can be swapped for another
 * while it is being populated, or to reproduce a client's report against
 * their own event. Deliberately NOT sticky (unlike the SDK override): a
 * remembered event pair would quietly make one admin's preview disagree
 * with everyone else's, with nothing on screen to say why.
 *
 * Both must be supplied together -- half an override is the cross-client
 * mismatch this module exists to prevent.
 */
export function resolvePreviewTarget () {
    try {
        const params = new URLSearchParams(window.location.search);
        const clientID = params.get(CLIENT_PARAM);
        const eventID = params.get(EVENT_PARAM);
        if (isID(clientID) && isID(eventID)) {
            return { clientID, eventID, isDemo: false };
        }
    } catch (e) {
        // Malformed URL: fall through to the demo pair.
    }

    return { clientID: DEMO_CLIENT_ID, eventID: DEMO_EVENT_ID, isDemo: true };
}

/** Invintus IDs are numeric strings; anything else is not an ID. */
function isID (value) {
    return typeof value === 'string' && /^[0-9]+$/.test(value);
}
